import { Card, CardBody } from '@heroui/react';
import React from 'react';

import { Input, TextArea } from '../../../../../../components/FormComponents';
import { Pill } from '../../../../../../components/Pill';
import { useSupplierAssortmentsDataQuery } from '../../../../../../generated';
import { useSupplierForm } from '../../SupplierFormProvider';
import { type TabProps } from '../../types';

export const ProfileTab: React.FC<TabProps> = ({ mode }) => {
  const { data: assortmentsData, loading: assortmentsLoading } =
    useSupplierAssortmentsDataQuery();

  const {
    formMethods: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useSupplierForm();

  // Watch form fields
  const companyName = watch('name');
  const companyDescription = watch('description');
  const primaryCategory = watch('primaryCategory');
  const secondaryCategories = watch('secondaryCategories') ?? [];
  const deliveryFee = watch('deliveryFee');
  const minimumOrderValue = watch('minimumOrderValue');
  const minimumOrderCurrency = watch('minimumOrderCurrency');

  const toggleCategory = (category: string) => {
    const newCategories = secondaryCategories.includes(category)
      ? secondaryCategories.filter((c: string) => c !== category)
      : [...secondaryCategories, category];
    setValue('secondaryCategories', newCategories);
  };

  return (
    <div className="max-w-4xl">
      {/* Cover Photo Section */}
      <Card className="mb-6">
        <CardBody className="p-0">
          {/* Cover Photo */}
          <div className="relative h-48 bg-gradient-to-r from-blue-400 to-blue-600 overflow-hidden">
            <div
              className="absolute inset-0 bg-blue-500 opacity-80"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ctext x='10' y='30' font-family='Arial, sans-serif' font-size='16' font-weight='bold'%3E${companyName}%3C/text%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat',
              }}
            />
          </div>
        </CardBody>
      </Card>

      {/* Form Section */}
      <div className="space-y-6">
        {/* Company Name */}
        <Input
          id="name"
          label="Company name"
          value={companyName}
          onChange={(value: string) => setValue('name', value)}
          placeholder="Company name"
          errorMessage={errors.name?.message}
          required
        />

        {/* Company Description */}
        <TextArea
          id="description"
          label="Company description"
          value={companyDescription}
          onChange={(value: string) => setValue('description', value)}
          placeholder="Add a one or two-line description about the supplier here"
          rows={3}
          errorMessage={errors.description?.message}
        />

        {/* Primary Category */}
        <Input
          id="primaryCategory"
          label="Primary category"
          value={primaryCategory}
          onChange={(value: string) => setValue('primaryCategory', value)}
          placeholder="Select primary category"
          errorMessage={errors.primaryCategory?.message}
          required
        />

        {/* Secondary Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Secondary category
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {assortmentsLoading ? (
              <div>Loading...</div>
            ) : (
              assortmentsData?.supplierAssortments.assortments.map(
                (category: string) => (
                  <Pill
                    key={category}
                    variant={
                      secondaryCategories.includes(category)
                        ? 'primary'
                        : 'default'
                    }
                    className="cursor-pointer"
                    onClick={() => toggleCategory(category)}
                  >
                    {category}
                  </Pill>
                )
              )
            )}
          </div>
          <p className="text-sm text-gray-500">Select as many as you like</p>
          {errors.secondaryCategories && (
            <p className="text-danger text-sm mt-1">
              {errors.secondaryCategories.message}
            </p>
          )}
        </div>

        {/* Delivery Fee */}
        <Input
          id="deliveryFee"
          label="Delivery fee"
          value={deliveryFee}
          onChange={(value: string) => {
            const numericValue = value.replace(/[^0-9.]/g, '');
            setValue('deliveryFee', numericValue);
          }}
          placeholder="Enter delivery fee"
          type="text"
          errorMessage={errors.deliveryFee?.message}
        />

        {/* Minimum Order Value */}
        <div className="flex gap-4">
          <Input
            id="minimumOrderValue"
            label="Minimum order value"
            value={minimumOrderValue}
            onChange={(value: string) => {
              const numericValue = value.replace(/[^0-9.]/g, '');
              setValue('minimumOrderValue', numericValue);
            }}
            placeholder="Enter minimum order value"
            type="text"
            className="flex-grow"
            errorMessage={errors.minimumOrderValue?.message}
          />
          <Input
            id="minimumOrderCurrency"
            label="Currency"
            value={minimumOrderCurrency}
            onChange={(value: string) =>
              setValue('minimumOrderCurrency', value)
            }
            placeholder="Select currency"
            className="w-32"
            errorMessage={errors.minimumOrderCurrency?.message}
          />
        </div>
      </div>
    </div>
  );
};
